import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { clamp, lerp, invLerp } from 'utils/helpers';
import { buildTimelineTicks } from 'utils/timelineTicks';
import DatePicker from 'components/uiComponents/Datepicker';
import classNames from 'classnames';

const formatDateYmd = (d) => {
  const date = d instanceof Date ? d : new Date(d);
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
  const yyyy = String(date.getFullYear());
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${yyyy}/${mm}/${dd}`;
};

const TimelineBar = ({ min, max, value, locked = false, onChange, onClose, sidebarIsOpened }) => {
  const trackRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [trackWidth, setTrackWidth] = useState(0);

  const minTs = +min;
  const maxTs = +max;
  const valueTs = +value;
  const isLockedRange = locked || minTs === maxTs;

  const { major, minor } = useMemo(() => buildTimelineTicks(min, max), [min, max]);
  const percent = useMemo(() => (isLockedRange ? 0.5 : clamp(invLerp(minTs, maxTs, valueTs), 0, 1)), [isLockedRange, minTs, maxTs, valueTs]);

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;

    const update = () => {
      try {
        const w = el.getBoundingClientRect().width;
        setTrackWidth(Number.isFinite(w) ? w : 0);
      } catch {
        setTrackWidth(0);
      }
    };

    update();

    let ro = null;
    if (typeof window !== 'undefined' && 'ResizeObserver' in window) {
      ro = new window.ResizeObserver(update);
      ro.observe(el);
    } else {
      window.addEventListener('resize', update);
    }

    return () => {
      try { ro?.disconnect?.(); } catch {}
      try { window.removeEventListener('resize', update); } catch {}
    };
  }, []);

  const setValueFromClientX = useCallback((clientX) => {
    if (isLockedRange) return;
    const trackEl = trackRef.current;
    if (!trackEl) return;

    const rect = trackEl.getBoundingClientRect();
    const x = clamp(clientX - rect.left, 0, rect.width);
    const t = rect.width ? x / rect.width : 0;
    const ts = Math.round(lerp(minTs, maxTs, t));
    onChange?.(new Date(ts));
  }, [isLockedRange, minTs, maxTs, onChange]);

  const onPointerDown = (e) => {
    if (isLockedRange) return;
    e.preventDefault();
    setIsDragging(true);
    setValueFromClientX(e.clientX);
  };

  const onPointerMove = useCallback((e) => {
    if (!isDragging) return;
    setValueFromClientX(e.clientX);
  }, [isDragging, setValueFromClientX]);

  const onPointerUp = useCallback(() => setIsDragging(false), []);

  useEffect(() => {
    if (!isDragging) return;
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
    return () => {
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
    };
  }, [isDragging, onPointerMove, onPointerUp]);

  const stepDays = 1;
  
  // Helper to compare dates by day (ignoring time)
  const compareDatesByDay = useCallback((date1, date2) => {
    const d1 = new Date(date1);
    const d2 = new Date(date2);
    d1.setHours(0, 0, 0, 0);
    d2.setHours(0, 0, 0, 0);
    return d1.getTime() - d2.getTime();
  }, []);
  
  // Check if we're at the boundaries (comparing by day only)
  const isAtMin = useMemo(() => {
    if (isLockedRange) return true;
    const valueDay = new Date(value);
    valueDay.setHours(0, 0, 0, 0);
    const minDay = new Date(min);
    minDay.setHours(0, 0, 0, 0);
    return valueDay.getTime() <= minDay.getTime();
  }, [value, min, isLockedRange]);
  
  const isAtMax = useMemo(() => {
    if (isLockedRange) return true;
    const valueDay = new Date(value);
    valueDay.setHours(0, 0, 0, 0);
    const maxDay = new Date(max);
    maxDay.setHours(0, 0, 0, 0);
    return valueDay.getTime() >= maxDay.getTime();
  }, [value, max, isLockedRange]);
  
  const stepBy = (sign) => {
    if (isLockedRange) return;
    
    // Don't step if already at boundary
    if (sign < 0 && isAtMin) return;
    if (sign > 0 && isAtMax) return;
    
    const next = new Date(value);
    next.setDate(next.getDate() + sign * stepDays);
    
    // Normalize to start of day for comparison
    const nextDay = new Date(next);
    nextDay.setHours(0, 0, 0, 0);
    const minDay = new Date(min);
    minDay.setHours(0, 0, 0, 0);
    const maxDay = new Date(max);
    maxDay.setHours(0, 0, 0, 0);
    
    // Clamp to boundaries
    if (nextDay.getTime() < minDay.getTime()) {
      onChange?.(new Date(minDay));
    } else if (nextDay.getTime() > maxDay.getTime()) {
      onChange?.(new Date(maxDay));
    } else {
      onChange?.(nextDay);
    }
  };

  const onDatePicked = useCallback((picked) => {
    if (!(picked instanceof Date) || Number.isNaN(picked.getTime())) return;
    const clamped = new Date(clamp(+picked, minTs, maxTs));
    onChange?.(clamped);
    setIsDatePickerOpen(false);
  }, [minTs, maxTs, onChange]);

  const majorTicks = useMemo(() => {
    // Keep the major tick marks, but show only those labels
    // that have enough horizontal space to avoid overlap.
    if (isLockedRange) {
      const d = value instanceof Date ? value : new Date(value);
      return [{ date: d, p: 50, showLabel: true }];
    }
    const w = Number.isFinite(trackWidth) ? trackWidth : 0;
    const minLabelSpacingPx = 44; // ~ label width + padding

    // Ensure we always have terminal ticks at min and max
    const ticks = [...major];
    const last = ticks[ticks.length - 1];
    const lastTs = last ? +last : null;
    if (lastTs !== maxTs) {
      ticks.push(new Date(maxTs));
    }

    // First pass: calculate positions for all ticks
    const tickPositions = ticks.map((date) => {
      const p = clamp(invLerp(minTs, maxTs, +date), 0, 1) * 100;
      const x = (p / 100) * w;
      return { date, p, x };
    });

    // Second pass: determine which labels to show, checking distance in both directions
    const showLabel = new Array(ticks.length).fill(false);
    
    if (w <= 0) {
      // If width is unknown, show all labels
      showLabel.fill(true);
    } else {
      // Always show first label
      showLabel[0] = true;
      
      // For middle labels, check distance from previous and to next
      for (let i = 1; i < tickPositions.length - 1; i++) {
        const prevX = showLabel[i - 1] ? tickPositions[i - 1].x : -1e9;
        const currX = tickPositions[i].x;
        const nextX = tickPositions[i + 1].x;
        
        // Show label if there's enough space from previous shown label
        // and enough space to next label (to avoid overlap)
        const hasSpaceFromPrev = currX - prevX >= minLabelSpacingPx;
        const hasSpaceToNext = nextX - currX >= minLabelSpacingPx;
        
        showLabel[i] = hasSpaceFromPrev && hasSpaceToNext;
      }
      
      // For last label, check distance from previous shown label
      const lastIdx = tickPositions.length - 1;
      if (lastIdx > 0) {
        // Find the last shown label before the end
        let prevShownX = -1e9;
        for (let i = lastIdx - 1; i >= 0; i--) {
          if (showLabel[i]) {
            prevShownX = tickPositions[i].x;
            break;
          }
        }
        const lastX = tickPositions[lastIdx].x;
        const hasSpaceFromPrev = lastX - prevShownX >= minLabelSpacingPx;
        
        // Show last label only if there's enough space from previous
        // or if it's the only way to show the max date
        showLabel[lastIdx] = hasSpaceFromPrev || (prevShownX === -1e9);
      } else {
        showLabel[lastIdx] = true;
      }
    }

    return tickPositions.map((tick, idx) => ({
      date: tick.date,
      p: tick.p,
      showLabel: showLabel[idx]
    }));
  }, [isLockedRange, major, minTs, maxTs, trackWidth, value]);

  return (
    <div className={classNames({
      "timelinebar": true,
      "timelinebar-sidebarIsOpened": !sidebarIsOpened,
    })}>
      <div className="timelinebar-wrapper">
        <div className="timelinebar__date">
          <button className="timelinebar__nav" onClick={() => stepBy(-1)} aria-label="Prev" disabled={isLockedRange || isAtMin}>{'<'}</button>
          <DatePicker
            value={value}
            isEditable={isDatePickerOpen}
            onSetValue={() => setIsDatePickerOpen(false)}
            onChangeValue={onDatePicked}
            onInputClick={() => setIsDatePickerOpen(true)}
            minDate={min}
            maxDate={max}
            shouldCloseOnSelect={true}
            popperPlacement="bottom"
            wrapperClassName="timelinebar__date-picker"
            inputClassName="timelinebar__date-label"
            popperClassName="timeline_date_calendar"
            dateFormat="yyyy/MM/dd"
            placeholderText="YYYY/MM/DD"
            title={formatDateYmd(value)}
          />
          <button className="timelinebar__nav" onClick={() => stepBy(1)} aria-label="Next" disabled={isLockedRange || isAtMax}>{'>'}</button>
        </div>

        <div className="timelinebar__track" ref={trackRef} onPointerDown={onPointerDown} role="slider" aria-valuemin={+min} aria-valuemax={+max} aria-valuenow={+value}>
          {!isLockedRange && minor.map((date) => {
            const p = clamp(invLerp(minTs, maxTs, +date), 0, 1) * 100;
            return <div key={`mn-${+date}`} className="timelinebar__tick timelinebar__tick--minor" style={{ left: `${p}%` }} />;
          })}
          {majorTicks.map(({ date, p, showLabel }) => {
            const label = date.toLocaleDateString('ru-RU', { month: '2-digit', year: '2-digit' });
            return (
              <div key={`mj-${+date}`} className="timelinebar__tick timelinebar__tick--major" style={{ left: `${p}%` }}>
                {showLabel && <span className="timelinebar__tick-label">{label}</span>}
              </div>
            );
          })}
          <div className="timelinebar__handle" style={{ left: `${percent * 100}%` }} onPointerDown={onPointerDown}>
            <div className="timelinebar__handle-icon">||</div>
          </div>
        </div>

        <button className="timelinebar__close" onClick={onClose} aria-label="Close">×</button>
      </div>
    </div>
  );
};

export default TimelineBar;